// Upstream: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/{field,input,native-select,textarea,checkbox}.tsx + cosscom/coss apps/origin/registry/default/components/comp-06.tsx — MIT.

import type { ComponentProps, ReactNode } from "react";
import {
  checkboxClass,
  fieldErrorClass,
  fieldHintClass,
  fieldLabelClass,
  inputClass,
  selectClass,
  selectIconClass,
  textareaClass,
} from "@ahcut/ui/recipes";
import { cn } from "@/lib/cn";
import { Glyph } from "./glyph";

type ControlSize = "sm" | "md";

export function Label({ className, ...rest }: ComponentProps<"label">) {
  return <label data-slot="label" className={cn(fieldLabelClass, className)} {...rest} />;
}

export interface InputProps extends Omit<ComponentProps<"input">, "size"> {
  size?: ControlSize;
}

export function Input({ size = "md", className, ...rest }: InputProps) {
  return <input data-slot="input" className={cn(inputClass({ size }), className)} {...rest} />;
}

export interface SelectProps extends Omit<ComponentProps<"select">, "size"> {
  size?: ControlSize;
}

/** Native <select>; the recipe removes the platform arrow, so the app's chevron glyph stands in. */
export function Select({ size = "md", className, children, ...rest }: SelectProps) {
  return (
    <div data-slot="select-wrapper" className="relative">
      <select data-slot="select" className={cn(selectClass({ size }), className)} {...rest}>
        {children}
      </select>
      <Glyph name="chevronDown" className={selectIconClass} />
    </div>
  );
}

export function Textarea({ className, ...rest }: ComponentProps<"textarea">) {
  return <textarea data-slot="textarea" className={cn(textareaClass, className)} {...rest} />;
}

export function Checkbox({ className, ...rest }: Omit<ComponentProps<"input">, "type">) {
  return <input type="checkbox" data-slot="checkbox" className={cn(checkboxClass, className)} {...rest} />;
}

/** Control id derived from the label — the same derivation the retired DS Field
 *  used, so every existing label/control association stays identical. */
export function fieldId(label: string): string {
  return `ahc-field-${label.replace(/\s+/g, "-").toLowerCase()}`;
}

/** Wiring a custom control receives from <Field> (see `children`). */
export interface FieldControlProps {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
}

export interface FieldProps extends Omit<InputProps, "children"> {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** Classes for the <input>; `className` styles the field wrapper. */
  inputClassName?: string;
  /** Render another control (Select, Textarea) wired to this label, hint and
   *  error. The input-only props are ignored when this is given. */
  children?: (control: FieldControlProps) => ReactNode;
}

/** Label + control + hint + error, connected with htmlFor / aria-describedby / aria-invalid. */
export function Field({
  label,
  hint,
  error,
  id,
  className,
  inputClassName,
  children,
  "aria-describedby": describedBy,
  "aria-invalid": ariaInvalid,
  ...inputProps
}: FieldProps) {
  const controlId = id ?? fieldId(label);
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const control: FieldControlProps = {
    id: controlId,
    "aria-describedby": [describedBy, hintId, errorId].filter(Boolean).join(" ") || undefined,
    "aria-invalid": error ? true : undefined,
  };

  return (
    <div data-slot="field" className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <Label htmlFor={controlId}>{label}</Label>
      {children ? (
        children(control)
      ) : (
        <Input
          {...inputProps}
          {...control}
          aria-invalid={error ? true : ariaInvalid}
          className={inputClassName}
        />
      )}
      {hint && (
        <p id={hintId} className={fieldHintClass}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={fieldErrorClass}>
          {error}
        </p>
      )}
    </div>
  );
}
